import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { BaseModal } from '../components';
import { generateDeferredPromise } from '../helpers/common.helpers';
import { MODAL_TYPES } from './constants';
import { ModalContext } from './Modal.context';

const { Provider } = ModalContext;

export const ModalProvider = ({ children }) => {
  const { t } = useTranslation();

  const modalDefaults = useMemo(
    () => ({
      icon: MODAL_TYPES.INFO,
      okButton: t('common:defaultModal.okButton'),
      cancelButton: t('common:defaultModal.cancelButton'),
    }),
    [t]
  );

  const [modalState, setModalState] = useState({
    ...modalDefaults,
    description: '',
    customContent: false,
    title: '',
    visible: false,
    customClass: '',
  });

  const deferredRef = useRef(null);

  const openModal = useCallback(
    (isConfirm, isCustom) => (title, description, config, customClass) => {
      if ([title, description].some((prop) => !prop)) {
        console.error('Modal requires at least title & description provided');
        return Promise.reject(new Error('Modal requires at least title & description provided'));
      }

      config = config || {};
      const deferred = generateDeferredPromise();
      deferredRef.current = deferred;

      setModalState({
        ...modalDefaults,
        icon: config.type || (isConfirm ? MODAL_TYPES.QUESTION : MODAL_TYPES.INFO),
        title,
        description: description,
        customContent: !!isCustom,
        okButton: config.okButton || modalDefaults.okButton,
        cancelButton: isConfirm ? config.cancelButton || modalDefaults.cancelButton : null,
        visible: true,
        customClass: customClass || '',
      });

      return deferred.promise;
    },
    [modalDefaults]
  );

  const closeModal = useCallback((result) => {
    if (deferredRef.current) {
      deferredRef.current.resolve(result);
      deferredRef.current = null;
    }
    setModalState((prevState) => ({ ...prevState, visible: false }));
  }, []);

  const contextValue = useMemo(
    () => ({
      openConfirmModal: openModal(true, false),
      openInfoModal: openModal(false, false),
      openCustomInfoModal: openModal(false, true),
    }),
    [openModal]
  );

  return (
    <Provider value={contextValue}>
      <BaseModal
        customClass={modalState.customClass}
        response={closeModal}
        visible={modalState.visible}
        icon={modalState.icon}
        title={modalState.title}
        description={modalState.description}
        customContent={modalState.customContent}
        okButton={modalState.okButton}
        cancelButton={modalState.cancelButton}
      />
      {children}
    </Provider>
  );
};

export default ModalProvider;
